import { Button, Modal, Tabs } from "antd";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setTimeRequest,
  getTimeRequest,
  eraseLogsRequest,
  numOfLogsRequest,
  weldParameterDownloadRequest,
  locationRequest,
  passwordProtectionRequest,
  commCheckRequest,
  getDeviceByIdRequest,
  clearCommandResponse,
  clearDeviceData,
} from "../../redux/deviceRegister/action";
import {
  ERASE_LOGS_REQUEST,
  GET_TIME_REQUEST,
  LOCATION_REQUEST,
  NUM_OF_LOGS_REQUEST,
  PASSWORD_PROTECTION_REQUEST,
  SET_TIME_REQUEST,
  WELD_PARAMETER_DOWNLOAD_REQUEST,
} from "../../redux/deviceRegister/types";
import CommonLoader from "../Loaders/loader";

const CommCheckModal = ({ isModalVisible, onCancel }) => {
  const dispatch = useDispatch();
  const { commCheckResult, isLoading, error, deviceData } = useSelector(
    (state) => state.deviceIdReducer
  );

  const [activeTab, setActiveTab] = React.useState("commcheck");
  const [deviceId, setDeviceId] = useState(null);
  const [tabPollingData, setTabPollingData] = useState({}); // Store polling intervals and timeouts per tab
  const [tabLoadingState, setTabLoadingState] = useState({}); // Track loading state per tab

  // Mapping of tab names to their corresponding device data fields
  const tabFieldMapping = {
    commcheck: "connection_status",
    settime: "set_time",
    gettime: "get_time",
    eraselogs: "erase_logs",
    numoflogs: "number_of_logs",
    weldparameterdownload: "weld_parameter_logs",
    location: "location",
    passwordprotection: "password_protection",
  };

  function toISO8601() {
    const d = new Date();
    const tz = -d.getTimezoneOffset();
    const pad = (n) => String(Math.floor(Math.abs(n))).padStart(2, "0");
    const sign = tz >= 0 ? "+" : "-";
    return (
      d.getFullYear() +
      "-" +
      pad(d.getMonth() + 1) +
      "-" +
      pad(d.getDate()) +
      "T" +
      pad(d.getHours()) +
      ":" +
      pad(d.getMinutes()) +
      ":" +
      pad(d.getSeconds()) +
      sign +
      pad(tz / 60) +
      ":" +
      pad(tz % 60)
    );
  }

  const basePayload = {
    Edge_UID: commCheckResult?.Edge_UID,
    Weld_UID: commCheckResult?.Weld_UID,
    Weld_Type: commCheckResult?.Weld_Type,
  };

  useEffect(() => {
    if (commCheckResult?.Edge_UID) {
      const newDeviceId = commCheckResult.Edge_UID;

      // If this is a different device, clear the old device data and update deviceId
      if (deviceId !== newDeviceId) {
        setDeviceId(newDeviceId);
        dispatch(clearDeviceData());
      }
    }
  }, [commCheckResult, deviceId, dispatch]);

  // Start polling for a specific tab
  const startPollingForTab = (tabName, edgeUID) => {
    console.log(`Starting callback-based polling for ${tabName}`);

    // Stop ALL existing polling first (only one tab should poll at a time)
    Object.keys(tabPollingData).forEach((tab) => {
      stopPollingForTab(tab);
    });

    const pollInterval = setInterval(() => {
      console.log(`Polling device for ${tabName}:`, edgeUID);
      dispatch(getDeviceByIdRequest({ deviceId: edgeUID }));
    }, 5000); // Poll every 5 seconds

    // Set timeout to stop polling after 1 minute
    const pollTimeout = setTimeout(() => {
      console.log(`Polling timeout reached for ${tabName} (1 minute)`);
      // Clear the interval directly since we have the reference
      clearInterval(pollInterval);
      clearTimeout(pollTimeout);

      // Update the tab status to show TimedOut
      setTabPollingData((prev) => ({
        ...prev,
        [tabName]: {
          interval: null,
          timeout: null,
          polling: false,
          timedOut: true,
        },
      }));
    }, 60000); // 1 minute

    // Store the interval and timeout for this tab
    setTabPollingData((prev) => ({
      ...prev,
      [tabName]: {
        interval: pollInterval,
        timeout: pollTimeout,
        polling: true,
        timedOut: false,
      },
    }));
  };

  // Stop polling for a specific tab
  const stopPollingForTab = (tabName) => {
    setTabPollingData((prev) => {
      const tabData = prev[tabName];
      if (tabData) {
        console.log(`Stopping polling for ${tabName}`);
        if (tabData.interval) {
          clearInterval(tabData.interval);
        }
        if (tabData.timeout) {
          clearTimeout(tabData.timeout);
        }
        return {
          ...prev,
          [tabName]: {
            ...tabData,
            polling: false,
            interval: null,
            timeout: null,
          },
        };
      }
      return prev;
    });
  };

  // Check if operation is complete and stop polling if needed
  const checkOperationComplete = (tabName) => {
    const fieldName = tabFieldMapping[tabName];
    let isComplete = false;

    if (tabName === "commcheck") {
      const status = deviceData?.connection_status;
      isComplete = status && status !== "In Progress";
    } else if (fieldName && deviceData?.[fieldName]) {
      const status = deviceData[fieldName]?.data;
      isComplete = status && status !== "In Progress";
    }

    if (isComplete && tabPollingData[tabName]?.polling) {
      console.log(`Operation completed for ${tabName}, stopping polling`);
      stopPollingForTab(tabName);
    }

    return isComplete;
  };

  // Monitor device data changes and stop polling when operations complete
  useEffect(() => {
    if (deviceData) {
      Object.keys(tabPollingData).forEach((tabName) => {
        if (tabPollingData[tabName]?.polling) {
          checkOperationComplete(tabName);
        }
      });
    }
  }, [deviceData, tabPollingData]);

  // Start initial polling if modal opens with "In Progress" CommCheck
  useEffect(() => {
    if (
      isModalVisible &&
      commCheckResult?.Data === "In Progress" &&
      commCheckResult?.Edge_UID
    ) {
      console.log("Modal opened with In Progress CommCheck, starting polling");
      const edgeUID = commCheckResult.Edge_UID;
      // Clear any existing device data when modal opens
      dispatch(clearDeviceData());

      if (!deviceId) {
        setDeviceId(edgeUID);
      }
      startPollingForTab("commcheck", edgeUID);
    }
  }, [isModalVisible, commCheckResult, dispatch]);

  useEffect(() => {
    if (!isModalVisible) {
      Object.keys(tabPollingData).forEach((tabName) => {
        stopPollingForTab(tabName);
      });
      setActiveTab("commcheck");
    }
  }, [isModalVisible]);

  const handleTabChange = (key) => {
    // Only return early for non-commcheck tabs if no commCheckResult
    if (key !== "commcheck" && (!commCheckResult || error)) return;

    const edgeUID = basePayload.Edge_UID || deviceId;

    // Success callback for command operations
    const onCommandSuccess = (tabName) => {
      return (responseData) => {
        console.log(
          `${tabName} command successful, response data:`,
          responseData
        );

        // Clear loading state for this tab
        setTabLoadingState((prev) => ({ ...prev, [tabName]: false }));

        // Only start polling if the operation is "In Progress"
        if (responseData?.Data === "In Progress") {
          console.log(`${tabName} is In Progress, starting polling`);
          if (edgeUID) {
            startPollingForTab(tabName, edgeUID);
          }
        } else {
          console.log(
            `${tabName} completed immediately with status: ${responseData?.Data}`
          );
          // Operation completed immediately, no need to poll
          // The response will be shown via the Redux store
        }
      };
    };

    // Clear old data and set loading state
    const clearTabDataAndSetLoading = (tabName, commandType) => {
      if (commandType) {
        dispatch(clearCommandResponse(commandType));
      }
      setTabLoadingState((prev) => ({ ...prev, [tabName]: true }));
    };

    const commandPayloads = {
      commcheck: () => {
        console.log("CommCheck clicked:", { deviceId, basePayload });
        clearTabDataAndSetLoading("commcheck");

        if (edgeUID) {
          console.log("Re-running CommCheck for Edge_UID:", edgeUID);
          dispatch(
            commCheckRequest(basePayload, onCommandSuccess("commcheck"))
          );
          if (!deviceId) {
            setDeviceId(edgeUID);
          }
        } else {
          console.log("No deviceId or Edge_UID available for CommCheck");
        }
      },
      settime: () => {
        console.log("SetTime clicked");
        clearTabDataAndSetLoading("settime", SET_TIME_REQUEST);
        dispatch(
          setTimeRequest(
            { ...basePayload, New_Time: toISO8601(new Date()) },
            onCommandSuccess("settime")
          )
        );
      },
      gettime: () => {
        console.log("GetTime clicked");
        clearTabDataAndSetLoading("gettime", GET_TIME_REQUEST);
        dispatch(getTimeRequest(basePayload, onCommandSuccess("gettime")));
      },
      eraselogs: () => {
        console.log("EraseLogs clicked");
        clearTabDataAndSetLoading("eraselogs", ERASE_LOGS_REQUEST);
        dispatch(eraseLogsRequest(basePayload, onCommandSuccess("eraselogs")));
      },
      numoflogs: () => {
        console.log("NumOfLogs clicked");
        clearTabDataAndSetLoading("numoflogs", NUM_OF_LOGS_REQUEST);
        dispatch(numOfLogsRequest(basePayload, onCommandSuccess("numoflogs")));
      },
      weldparameterdownload: () => {
        console.log("WeldParameterDownload clicked");
        clearTabDataAndSetLoading(
          "weldparameterdownload",
          WELD_PARAMETER_DOWNLOAD_REQUEST
        );
        dispatch(
          weldParameterDownloadRequest(
            {
              ...basePayload,
              Command: "WeldParameterDownload",
              Password: "default_password",
            },
            onCommandSuccess("weldparameterdownload")
          )
        );
      },
      location: () => {
        console.log("Location clicked");
        clearTabDataAndSetLoading("location", LOCATION_REQUEST);
        dispatch(locationRequest(basePayload, onCommandSuccess("location")));
      },
      passwordprotection: () => {
        console.log("PasswordProtection clicked");
        clearTabDataAndSetLoading(
          "passwordprotection",
          PASSWORD_PROTECTION_REQUEST
        );
        dispatch(
          passwordProtectionRequest(
            basePayload,
            onCommandSuccess("passwordprotection")
          )
        );
      },
    };

    if (commandPayloads[key]) {
      commandPayloads[key]();
    }
    setActiveTab(key);
  };

  const renderApiResponse = (commandType) => {
    const fieldName = tabFieldMapping[commandType];
    const deviceField = deviceData?.[fieldName];
    const isPollingThisTab = tabPollingData[commandType]?.polling;
    const hasTimedOut = tabPollingData[commandType]?.timedOut;
    const isTabLoading = tabLoadingState[commandType];

    if (isLoading || isTabLoading) {
      return (
        <div className="manage-loader">
          <CommonLoader />
        </div>
      );
    }

    if (error) {
      return <div className="error">Error: {error}</div>;
    }

    // Show loader if no device data available or if device data doesn't match current device
    const currentDeviceId = commCheckResult?.Edge_UID || deviceId;
    const deviceDataMatches = deviceData?.deviceId === currentDeviceId;

    if (!deviceData || !deviceDataMatches) {
      return (
        <div className="modal-tab-content">
          <div style={{ marginTop: 16 }}>
            <div className="commcheck-info-grid">
              <div>
                <strong>Device ID:</strong>
              </div>
              <div>{commCheckResult?.Edge_UID || "-"}</div>
              <div>
                <strong>Weld UID:</strong>
              </div>
              <div>{commCheckResult?.Weld_UID || "-"}</div>
              <div>
                <strong>Weld Type:</strong>
              </div>
              <div>{commCheckResult?.Weld_Type || "-"}</div>
              <div>
                <strong>Project:</strong>
              </div>
              <div>-</div>
              <div>
                <strong>Operation Status:</strong>
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <div className="loader">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div>
                <strong>Last Updated:</strong>
              </div>
              <div>-</div>
            </div>
          </div>
        </div>
      );
    }

    // Always show device data structure
    let parsedLocationData = {};
    if (deviceField?.data && typeof deviceField.data === "string") {
      try {
        parsedLocationData = JSON.parse(deviceField.data);
      } catch (e) {
        console.log("Failed to parse location data:", e);
      }
    }

    const displayData = {
      "Device ID": deviceData?.deviceId || commCheckResult?.Edge_UID || "-",
      "Weld UID": deviceData?.weld_uid || commCheckResult?.Weld_UID || "-",
      "Weld Type": deviceData?.weld_type || commCheckResult?.Weld_Type || "-",
      Project: deviceData?.projectName || "-",
      ...(parsedLocationData && Object.keys(parsedLocationData).length > 0
        ? Object.entries(parsedLocationData).reduce((acc, [key, value]) => {
            acc[key] = value || "-";
            return acc;
          }, {})
        : {
            "Operation Status": (() => {
              let status = "In Progress";

              if (deviceField?.data) {
                status = deviceField.data;
              } else if (deviceData?.connection_status) {
                status = deviceData.connection_status;
              }

              // Override with TimedOut if polling timed out
              if (hasTimedOut && status === "In Progress") {
                status = "TimedOut";
              }

              return status;
            })(),
          }),
      ...(deviceField?.error && deviceField.data !== "In Progress"
        ? {
            "Error Details": deviceField.error,
          }
        : {}),
      "Last Updated": deviceData?.updatedAt
        ? new Date(deviceData.updatedAt).toLocaleString()
        : "-",
    };

    return (
      <div className="modal-tab-content">
        <div style={{ marginTop: 16 }}>
          <div className="commcheck-info-grid">
            {Object.entries(displayData).map(([key, value]) => (
              <React.Fragment key={key}>
                <div>
                  <strong>{key}:</strong>
                </div>
                <div>
                  {key === "Operation Status" ? (
                    <span
                      className={`commcheck-status ${
                        value === "Success" || value === "Online"
                          ? "success"
                          : value === "In Progress"
                          ? "warning"
                          : value === "TimedOut"
                          ? "error"
                          : "error"
                      }`}
                    >
                      {String(value)}
                      {value === "In Progress" &&
                        isPollingThisTab &&
                        activeTab === commandType &&
                        " (Checking...)"}
                    </span>
                  ) : key === "Error Details" ? (
                    <span className="commcheck-status error">
                      {String(value)}
                    </span>
                  ) : (
                    <span>{String(value)}</span>
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const tabItems = [
    {
      key: "commcheck",
      label: "CommCheck",
      children: (
        <div className="modal-tab-content">
          {isLoading ? (
            <div className="manage-loader">
              <CommonLoader />
            </div>
          ) : deviceData && deviceData.deviceId === (commCheckResult?.Edge_UID || deviceId) ? (
            <div style={{ marginTop: 16 }}>
              <div className="commcheck-info-grid">
                <div>
                  <strong>Device ID:</strong>
                </div>
                <div>{deviceData.deviceId}</div>
                <div>
                  <strong>Weld UID:</strong>
                </div>
                <div>{deviceData.weld_uid || "-"}</div>
                <div>
                  <strong>Weld Type:</strong>
                </div>
                <div>{deviceData.weld_type || "-"}</div>
                <div>
                  <strong>Project:</strong>
                </div>
                <div>{deviceData.projectName || "-"}</div>
                <div>
                  <strong>Connection Status:</strong>
                </div>
                <div>
                  {(() => {
                    // Show device connection status only
                    let status = deviceData.connection_status || "In Progress";

                    // Override with TimedOut if polling timed out
                    if (
                      tabPollingData.commcheck?.timedOut &&
                      status === "In Progress"
                    ) {
                      status = "TimedOut";
                    }

                    const statusClass =
                      status === "Online" || status === "Success"
                        ? "success"
                        : status === "In Progress"
                        ? "warning"
                        : status === "TimedOut"
                        ? "error"
                        : "error";

                    return (
                      <span className={`commcheck-status ${statusClass}`}>
                        {status}
                        {status === "In Progress" &&
                          tabPollingData.commcheck?.polling &&
                          " (Checking...)"}
                      </span>
                    );
                  })()}
                </div>
                <div>
                  <strong>Last Updated:</strong>
                </div>
                <div>{new Date(deviceData.updatedAt).toLocaleString()}</div>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 16 }}>
              <div className="commcheck-info-grid">
                <div>
                  <strong>Device ID:</strong>
                </div>
                <div>{commCheckResult?.Edge_UID || "-"}</div>
                <div>
                  <strong>Weld UID:</strong>
                </div>
                <div>{commCheckResult?.Weld_UID || "-"}</div>
                <div>
                  <strong>Weld Type:</strong>
                </div>
                <div>{commCheckResult?.Weld_Type || "-"}</div>
                <div>
                  <strong>Project:</strong>
                </div>
                <div>-</div>
                <div>
                  <strong>Connection Status:</strong>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div className="loader">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </div>
                </div>
                <div>
                  <strong>Last Updated:</strong>
                </div>
                <div>-</div>
              </div>
            </div>
          )}

          {error && (
            <div className="commcheck-error">
              <b>Error:</b> {error}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "settime",
      label: "SetTime",
      children: renderApiResponse("settime", "Set Time"),
    },
    {
      key: "gettime",
      label: "GetTime",
      children: renderApiResponse("gettime", "Get Time"),
    },
    {
      key: "eraselogs",
      label: "EraseLogs",
      children: renderApiResponse("eraselogs", "Erase Logs"),
    },
    {
      key: "numoflogs",
      label: "NumOfLogs",
      children: renderApiResponse("numoflogs", "Number of Logs"),
    },
    {
      key: "weldparameterdownload",
      label: "WeldParameterDownload",
      children: renderApiResponse(
        "weldparameterdownload",
        "Weld Parameter Download"
      ),
    },
    {
      key: "location",
      label: "Location",
      children: renderApiResponse("location", "Location"),
    },
    {
      key: "passwordprotection",
      label: "PasswordProtection",
      children: renderApiResponse("passwordprotection", "Password Protection"),
    },
  ];

  return (
    <Modal
      title="Manage Edge Device"
      open={isModalVisible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>
      ]}
      width={800}
    >
      <Tabs
        className="manage-edge-device"
        activeKey={activeTab}
        items={tabItems}
        onChange={handleTabChange}
      />
    </Modal>
  );
};

export default CommCheckModal;
